import type { ShouldRevalidateFunctionArgs } from "react-router";
import { z } from "zod";

/**
 * A Command intent's submission body as the router hands it over — `json` is
 * whatever was posted, so it is parsed rather than inspected. `loose` keeps the rest
 * of the payload, because which keys an `update_lead` carries is itself the signal
 * that decides whether the unassigned badge went stale.
 */
const CommandSubmission = z.looseObject({
  // `.catch` rather than a plain optional: a payload whose `intent` is not a string is
  // still a Command submission that named no intent, and must be read as one rather
  // than mistaken for a Form route's multipart post.
  intent: z.string().optional().catch(undefined),
});

/** True for /lead-management and its child paths (/lead-management/:tab, …). */
export function isLeadManagementPath(pathname: string) {
  return pathname === "/lead-management" || pathname.startsWith("/lead-management/");
}

export function isLeadManagementFormAction(formAction: string | undefined, currentUrl: URL) {
  if (!formAction) return false;

  // Mutations post either to the /lead-management/:tab page route (the Command
  // intents) or to one of its two lead Form routes (/…/new, /…/:leadId/edit) — never
  // to the loader-only /lead-management/* resource routes — so a path-prefix match is
  // exact enough.
  return isLeadManagementPath(new URL(formAction, currentUrl).pathname);
}

function getLeadManagementIntent({
  currentUrl,
  formMethod,
  formAction,
  json,
}: ShouldRevalidateFunctionArgs) {
  if (!formMethod || formMethod === "GET" || !isLeadManagementFormAction(formAction, currentUrl)) {
    return undefined;
  }

  return CommandSubmission.safeParse(json).data?.intent;
}

/**
 * Whether an on-demand /lead-management read should re-run after a mutation.
 *
 * A Command intent names itself, so the caller's set decides. A lead Form route's
 * submission is multipart and carries no intent, so `getLeadManagementIntent` answers
 * `undefined` and the router's default applies — which after an action is "yes", and is
 * the right answer: creating a lead or saving one changes the stats, the task queue and
 * the lead's own detail read alike.
 */
export function shouldRevalidateForLeadIntents(
  args: ShouldRevalidateFunctionArgs,
  intents: ReadonlySet<string>,
) {
  if (
    args.formMethod &&
    args.formMethod !== "GET" &&
    isLeadManagementFormAction(args.formAction, args.currentUrl)
  ) {
    if (args.actionStatus && args.actionStatus >= 400) return args.defaultShouldRevalidate;
    const intent = getLeadManagementIntent(args);

    return intent ? intents.has(intent) : args.defaultShouldRevalidate;
  }

  return args.defaultShouldRevalidate;
}

// Shared by the initial app-layout loader and the targeted polling resource so
// unrelated actions cannot fan out into duplicate count queries.
export function shouldRevalidateUnassignedCount({
  currentUrl,
  nextUrl,
  formMethod,
  formAction,
  json,
  actionStatus,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) {
  if (formMethod && formMethod !== "GET") {
    if (actionStatus && actionStatus >= 400) return defaultShouldRevalidate;

    if (isLeadManagementFormAction(formAction, currentUrl)) {
      const submission = CommandSubmission.safeParse(json);

      if (submission.success) {
        // A Command intent, which says exactly what it did.
        const { intent } = submission.data;

        if (intent === "assign" || intent === "purge_leads" || intent === "merge_leads")
          return true;

        if (intent === "update_lead") {
          return "assigned_to" in submission.data || "status" in submission.data;
        }

        return false;
      }

      // A lead Form route's submission. It is multipart, so there is no intent to read —
      // and the two forms are the only non-JSON posts under /lead-management. Adding a
      // lead as an admin creates an unassigned one by construction, so the badge is
      // stale; the edit form cannot change assignment, so refreshing after it is one
      // count query more than strictly needed and not worth a second mechanism to avoid.
      return true;
    }

    return defaultShouldRevalidate;
  }

  if (isLeadManagementPath(currentUrl.pathname) && isLeadManagementPath(nextUrl.pathname)) {
    return false;
  }

  if (currentUrl.pathname === nextUrl.pathname) return defaultShouldRevalidate;

  return false;
}
